import { css } from '@emotion/react';
import { responsiveStyles } from '@polestar/component-warehouse-react/helpers/responsiveStyle';
import { createCSSTransition, MotionDurations, MotionEasings, Transitions, } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import { animate, m as motion, transform, useMotionValue, useTransform, } from 'framer-motion';
import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import Section, { MediaSection, } from '@polestar/component-warehouse-react/components/section/Section';
import Sizes from '@polestar/component-warehouse-react/tokens/sizes/Sizes';
import SectionSpacer from '@polestar/component-warehouse-react/components/section-spacer/SectionSpacer';
import styled from '@emotion/styled';
import ResizeObserver from 'resize-observer-polyfill';
import ParallaxRange from '@polestar/component-warehouse-react/components/parallax-range/ParallaxRange';
import { useParallaxRange } from '@polestar/component-warehouse-react/components/parallax-range/ParallaxRangeContext';
import { shouldNotForwardProps } from '@polestar/component-warehouse-react/helpers/shouldForwardProps';
import useMutableValue from '@polestar/component-warehouse-react/hooks/useMutableValue';
import useResizeCallback, { ResizeCallbackTypes, } from '@polestar/component-warehouse-react/hooks/useResizeCallback';
import { useHeightCallback } from '@polestar/component-warehouse-react/hooks/useDimensions';
/**
 * @deprecated use ParallaxZoomedMediaSectionScaleFrom
 */
export var ParallaxMediaSectionScaleFrom;
(function (ParallaxMediaSectionScaleFrom) {
    ParallaxMediaSectionScaleFrom["content"] = "content";
    ParallaxMediaSectionScaleFrom["media"] = "media";
})(ParallaxMediaSectionScaleFrom || (ParallaxMediaSectionScaleFrom = {}));
/**
 * @deprecated use ParallaxZoomedMediaSectionScaleTo
 */
export var ParallaxMediaSectionScaleTo;
(function (ParallaxMediaSectionScaleTo) {
    ParallaxMediaSectionScaleTo["media"] = "media";
    ParallaxMediaSectionScaleTo["fullscreen"] = "fullscreen";
})(ParallaxMediaSectionScaleTo || (ParallaxMediaSectionScaleTo = {}));
export var ParallaxZoomedMediaSectionScaleFrom;
(function (ParallaxZoomedMediaSectionScaleFrom) {
    ParallaxZoomedMediaSectionScaleFrom["content"] = "content";
    ParallaxZoomedMediaSectionScaleFrom["media"] = "media";
})(ParallaxZoomedMediaSectionScaleFrom || (ParallaxZoomedMediaSectionScaleFrom = {}));
export var ParallaxZoomedMediaSectionScaleTo;
(function (ParallaxZoomedMediaSectionScaleTo) {
    ParallaxZoomedMediaSectionScaleTo["media"] = "media";
    ParallaxZoomedMediaSectionScaleTo["fullscreen"] = "fullscreen";
})(ParallaxZoomedMediaSectionScaleTo || (ParallaxZoomedMediaSectionScaleTo = {}));
// is needed because you could get single pixel line
const SCALE_MARGIN = 2; //px
const ParallaxZoomedMediaSection = forwardRef(({ range = '200vh', ...props }, ref) => {
    const parallaxTransformer = useMutableValue(null);
    const callbackRef = useResizeCallback((node) => {
        if (node) {
            const parallaxThresshold = window.innerHeight / (node.clientHeight + window.innerHeight);
            parallaxTransformer.set(transform([0, parallaxThresshold, 1 - parallaxThresshold, 1], ['-40%', '0%', '0%', '40%']));
        }
    }, [parallaxTransformer], ResizeCallbackTypes.height);
    return (React.createElement(ParallaxRange, { ref: callbackRef, range: range },
        React.createElement(ParallaxZoomedMediaSectionContent, { ref: ref, ...props, parallaxTransformer: parallaxTransformer })));
});
ParallaxZoomedMediaSection.displayName = 'ParallaxZoomedMediaSection';
export default ParallaxZoomedMediaSection;
/**
 * @deprecated use ParallaxZoomedMediaSection
 */
export const ParallaxMediaSection = ParallaxZoomedMediaSection;
const ParallaxZoomedMediaSectionContent = forwardRef(({ sectionSpacing = Sizes.medium, parallaxIn = [0, 0.5], scaleFrom = ParallaxZoomedMediaSectionScaleFrom.media, scaleTo = ParallaxZoomedMediaSectionScaleTo.fullscreen, scaleToSpacing = null, animateInOnIntersect = false, animateInOnIntersectTransition = Transitions.largeSurface, children, outBackgroundColor, animateBackToOriginal = false, inThreshold = 0.33, parallaxTransformer, overlay, ...props }, ref) => {
    const { outerProgress: rangeProgress, progress: innerRangeProgress } = useParallaxRange();
    const internalProgress = useMotionValue(0);
    const progress = animateInOnIntersect ? internalProgress : rangeProgress;
    useEffect(() => {
        if (animateInOnIntersect) {
            let animation = null;
            let currentTo = -1;
            return rangeProgress.on('change', (value) => {
                const to = value > 0 && value < 1 ? 1 : animateBackToOriginal || value < 1 ? 0 : 1;
                // No need to trigger it multiple times
                if (currentTo === to) {
                    return;
                }
                currentTo = to;
                if (animation) {
                    animation.stop();
                }
                animation = animate(internalProgress, to, animateInOnIntersectTransition);
            });
        }
    }, [
        animateBackToOriginal,
        animateInOnIntersect,
        animateInOnIntersectTransition,
        internalProgress,
        rangeProgress,
    ]);
    // We have to use the container height which is 100vh, instead of window.innerHeight directly
    // because mobile browsers report difference values for innerHeight, but the 100vh doesn't change
    const [heightRef, innerWindowHeight] = useHeightCallback();
    const windowHeight = innerWindowHeight + SCALE_MARGIN;
    const [[contentWidth, contentHeight], setContentDimensions] = useState([0, 0]);
    const [[outerWidth, outerHeight], setOuterDimensions] = useState([0, 0]);
    const [[innerWidth, innerHeight], setInnerDimensions] = useState([0, 0]);
    // We need to use refs because there is a bug in framer
    // Send a PR here: https://github.com/framer/motion/pull/1084
    const contentRef = useRef(null);
    const outerRef = useRef(null);
    const innerRef = useRef(null);
    const innerMultiplier = useMutableValue(1);
    const outerMultiplier = useMutableValue(1);
    useEffect(() => {
        let resizeObserver = null;
        const calculateDimension = () => {
            if (outerRef.current) {
                setOuterDimensions([outerRef.current.offsetWidth, outerRef.current.offsetHeight]);
            }
            if (innerRef.current) {
                setInnerDimensions([innerRef.current.offsetWidth, innerRef.current.offsetHeight]);
            }
            if (contentRef.current) {
                setContentDimensions([contentRef.current.offsetWidth, contentRef.current.offsetHeight]);
            }
        };
        calculateDimension();
        window.addEventListener('resize', calculateDimension);
        if (innerRef.current) {
            resizeObserver = new ResizeObserver(calculateDimension);
            resizeObserver.observe(innerRef.current);
        }
        return () => {
            window.removeEventListener('resize', calculateDimension);
            resizeObserver === null || resizeObserver === void 0 ? void 0 : resizeObserver.disconnect();
        };
    }, []);
    const scaleToWidth = contentWidth + (scaleTo === ParallaxZoomedMediaSectionScaleTo.fullscreen ? SCALE_MARGIN : 0);
    const scaleToHeight = windowHeight - contentHeight;
    const elementRatio = outerWidth / outerHeight;
    const elementInnerRatio = innerWidth / innerHeight;
    const windowRatio = scaleToWidth / scaleToHeight;
    const sizeOnX = elementRatio > elementInnerRatio;
    const sizeOnXWindow = windowRatio > elementInnerRatio;
    // This is to make the content cover the component
    innerMultiplier.set(sizeOnX
        ? // Size on left/right
            outerWidth / innerWidth
        : // Size on top, bottom
            outerHeight / innerHeight);
    // This is to make the content cover when fullscreen
    outerMultiplier.set(sizeOnXWindow
        ? // Size on left/right
            scaleToWidth / innerWidth
        : // Size on top, bottom
            scaleToHeight / innerHeight);
    const backgroundColor = useTransform(rangeProgress, (latestRangeProgress) => {
        return (animateBackToOriginal && latestRangeProgress >= 0.5) ||
            (!animateBackToOriginal && latestRangeProgress >= 1)
            ? outBackgroundColor
            : '';
    });
    // translate the 0-1 to 0-2 to animate in & out in 1 animation
    const translatedProgress = useTransform(progress, [0, inThreshold, 1 - inThreshold, 1], [0, 1, 1, 0]);
    // Use only translatedProgress when we need to animate back to original scale
    const scaleProgress = animateBackToOriginal && !animateInOnIntersect ? translatedProgress : progress;
    const scaleX = useTransform(scaleProgress, parallaxIn, [
        1,
        outerWidth ? scaleToWidth / outerWidth : 1,
    ]);
    const scaleY = useTransform(scaleProgress, parallaxIn, [
        1,
        outerWidth ? scaleToHeight / outerHeight : 1,
    ]);
    // Invert the scaling of the outside component
    const invertedScaleProgress = useTransform(scaleProgress, parallaxIn, [0, 1]);
    const scaleInner = useCallback(([latestScale, latestScaleProgress]) => {
        const multiplier = innerMultiplier.get() +
            (outerMultiplier.get() - innerMultiplier.get()) * latestScaleProgress || 1;
        return multiplier * (1 / latestScale);
    }, [innerMultiplier, outerMultiplier]);
    // Overload matching for useTransform is incorrect for framer typing https://github.com/framer/motion/issues/840
    const scaleInnerX = useTransform([scaleX, invertedScaleProgress], scaleInner);
    const scaleInnerY = useTransform([scaleY, invertedScaleProgress], scaleInner);
    // @ts-ignore
    const parallaxY = useTransform([innerRangeProgress, parallaxTransformer], ([latestInnerRangeProgress, latestParallaxTransformer]) => {
        return latestParallaxTransformer ? latestParallaxTransformer(latestInnerRangeProgress) : 0;
    });
    return (React.createElement(React.Fragment, null,
        React.createElement(Container, { ref: heightRef, style: {
                backgroundColor,
            } },
            React.createElement(StyledSection, { ref: ref, ...props, media: scaleFrom === ParallaxZoomedMediaSectionScaleFrom.media },
                !!sectionSpacing && React.createElement(SectionSpacer, { size: sectionSpacing }),
                React.createElement(OuterWrapper, null,
                    React.createElement(Outer, { ref: outerRef, style: {
                            scaleX,
                            scaleY,
                        } },
                        React.createElement(motion.div, { ref: innerRef, style: {
                                opacity: innerMultiplier.get() ? 1 : 0,
                                scaleX: scaleInnerX,
                                scaleY: scaleInnerY,
                                y: parallaxY,
                            } }, children)),
                    !!overlay && React.createElement(Overlay, null, overlay)),
                !!sectionSpacing && React.createElement(SectionSpacer, { size: sectionSpacing }))),
        scaleTo === ParallaxZoomedMediaSectionScaleTo.media ? (React.createElement(StyledMediaSection, { "aria-hidden": true },
            React.createElement(ScaleToSizer, { ref: contentRef, scaleTo: scaleTo, spacing: scaleToSpacing }))) : (React.createElement(ScaleToSizer, { "aria-hidden": true, ref: contentRef, scaleTo: scaleTo, spacing: scaleToSpacing }))));
});
ParallaxZoomedMediaSectionContent.displayName = 'ParallaxZoomedMediaSectionContent';
const Container = styled(motion.div) `
  overflow: hidden;
  transition: ${createCSSTransition('background-color', MotionEasings.default, MotionDurations.xFast)};
`;
const OuterWrapper = styled.div `
  position: relative;
  flex: 1;
  display: flex;
  /* This forces a flex-grow:0 on the children */
  height: 0;
`;
const Overlay = styled.div `
  position: absolute;
  inset-block-start: 0;
  inset-inline-start: 0;
  inset-inline-end: 0;
  inset-block-end: 0;

  pointer-events: none;

  > * {
    pointer-events: auto;
  }
`;
const StyledSection = styled(Section)(({ media }) => css(css `
      height: 100vh;
      display: flex;
      flex-direction: column;
      position: relative;
    `, !media &&
    css `
        > div {
          height: 100%;
          display: flex;
          flex-direction: column;
        }
      `));
const Outer = styled(motion.div) `
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  line-height: 0;
`;
const StyledMediaSection = styled(MediaSection) `
  pointer-events: none;
  visibility: hidden;
  height: 0;
  overflow: hidden;
`;
const ScaleToSizer = styled('div', shouldNotForwardProps(true, 'spacing'))(({ scaleTo, spacing }) => css(css `
      width: 100%;
    `, scaleTo === ParallaxZoomedMediaSectionScaleTo.fullscreen &&
    spacing &&
    css `
        position: absolute;
        ${responsiveStyles({
        maxWidth: spacing,
        paddingBlockStart: spacing,
        paddingBlockEnd: spacing,
    }, {
        maxWidth: (value) => `calc(100vw - (${value} * 2))`,
    })}
      `, scaleTo === ParallaxZoomedMediaSectionScaleTo.media &&
    spacing &&
    css `
        ${responsiveStyles({
        paddingBlockStart: spacing,
        paddingBlockEnd: spacing,
    })}
      `));
